import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { checkIsTariffApplied, getStationDetailsByCode } from '../../../services/search-page/search-page-service';
import { getConfigurationDetail } from '../../../services/common/common-service';
import { Common } from '../../../utils/constants';
import type { IStationDetailsModel } from '../../../models/search-page';
import type { IStationDetails } from '../../../models/station-details';
import type { ICommonProps } from '../../../types/propTypes';

export const useStationDetails = (props: ICommonProps) => {
    const { t } = props;
    const { code } = useParams();
    const navigate = useNavigate();
    const [stationDetails, setStationDetails] = useState<IStationDetailsModel | null>(null);
    const [defaultLocationImageURL, setdefaultLocationImageURL] = useState<string>('');
    const [showCardValidationModal, setShowCardValidationModal] = useState<boolean>(false);
    const [holding, setHolding] = useState<boolean>(false);
    const [selectedConnector, setSelectedConnector] = useState<number>(0);
    const [connectorId, setConnectorId] = useState<number>(0);
    const [showModal, setShowModal] = useState<boolean>(false);

    useEffect(() => {
        document.body.style.overflow = 'auto';
        if (code && code.length > 10) {
            toast.error(t<string>('stationDetails.error.invalidCode'));
            navigate('/');
        } else {
            const setStationDetailsByCode = async (code: string) => {
                await getStationDetailsByCode(code)
                    .then((res) => {
                        if (res.Data && Object.keys(res.Data).length !== 0) {
                            setStationDetails(res.Data);
                            getDefaultLocationImage();
                            if (
                                res.Data.charge_station_status &&
                                res.Data.charge_station_status &&
                                res.Data.charge_station_status.code === Common.ChargeStationStatus.CHARGING
                            ) {
                                toggleCardValidationModal(true);
                            }
                        } else {
                            toast.error(t<string>('stationDetails.error.stationNotFound'));
                            navigate('/');
                        }
                    })
                    .catch((m) => {
                        if (m.response && m.response.data && m.response.data.message) {
                            toast.error(m.response.data.message);
                        } else {
                            toast.error(m.toString());
                        }
                        navigate('/');
                    });
            };
            const getDefaultLocationImage = async () => {
                await getConfigurationDetail({
                    code: Common.ConfigurationKey.DEFAULT_LOCATION_IMAGE,
                })
                    .then((res) => {
                        setdefaultLocationImageURL(res.Data[0]?.value);
                    })
                    .catch((e) => {
                        console.error('error', e);
                    });
            };

            setStationDetailsByCode(code || '');
        }
    }, []);

    useEffect(() => {
        if (stationDetails && selectedConnector === 0 && shouldAutoSelectConnector(stationDetails)) {
            setSelectedConnector(stationDetails.connectors[0].id);
        }
    }, [stationDetails]);

    const shouldAutoSelectConnector = (stationData?: IStationDetails): boolean =>
        !!stationData?.connectors?.length &&
        stationData.connectors.length === 1 &&
        stationData.charge_station_status?.code === Common.ChargeStationStatus.AVAILABLE;

    const handleHideTariffModal = () => {
        showTariffBreakDownDetailsModal(false);
        document.body.style.overflow = 'auto';
        setSelectedConnector(shouldAutoSelectConnector(stationDetails) ? stationDetails.connectors[0].id : 0);
    };

    const backToHome = () => {
        navigate('/');
    };

    const toggleCardValidationModal = (showModal: boolean) => {
        setShowCardValidationModal(showModal);
    };

    const startHold = () => {
        if (selectedConnector === 0) {
            return;
        }
        setHolding(true);
    };

    const stopHold = () => {
        setHolding(false);
    };

    const showTariffBreakDownDetailsModal = (showModal: boolean) => {
        setShowModal(showModal);
    };

    const checkTariffAvailability = async () => {
        await checkIsTariffApplied(selectedConnector)
            .then((res) => {
                if (res.Data.available) {
                    navigate(`start-charge/${selectedConnector}`, {
                        state: stationDetails,
                    });
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            })
            .finally(() => {
                stopHold();
            });
    };

    const handleSelectConnector = (connectorId: number) => {
        if (selectedConnector === connectorId) {
            setSelectedConnector(0);
            return;
        }
        if (stationDetails.charge_station_status.code === Common.ChargeStationStatus.AVAILABLE) {
            setSelectedConnector(connectorId);
        }
    };

    return {
        t,
        stationDetails,
        defaultLocationImageURL,
        showCardValidationModal,
        holding,
        selectedConnector,
        connectorId,
        showModal,
        setSelectedConnector,
        setConnectorId,
        shouldAutoSelectConnector,
        handleHideTariffModal,
        backToHome,
        toggleCardValidationModal,
        startHold,
        stopHold,
        showTariffBreakDownDetailsModal,
        checkTariffAvailability,
        handleSelectConnector,
    };
};
